import React from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Layout, Table, Button, Tag, Row, Col, message, Space, Modal, Select } from "antd";
import Search from "antd/es/input/Search";
import { PlusOutlined, DownloadOutlined, SettingOutlined, FilePdfOutlined } from "@ant-design/icons";
import { useReactToPrint } from "react-to-print";
import { Header, AutoCompleteCompany } from "components";
import { RewardVoucherQrCodePrint } from "components/reward/RewardVoucherQrCodePrint";
import { listCampaigns, exportCampaigns, cancelCampaign } from "services/reward-campaign-service";
import { getTotalBalance, getRewardBalance } from "services/company-service";
import { CardInfo } from "components/card/card-info";
import { formatMoney, formatDate, exportXls, getMaskedCpf } from "utils";
import { MONTHLY_REVENUE_RANGE_DATA } from "utils/static-data";
import RewardCampaignForm from "./reward-campaign-form";
import RewardCampaignConfigDrawer from "./reward-campaign-config-drawer";

const { Content, Footer } = Layout;

const mapStateToProps = (state) => ({
  login: state.authReducer.loginReducer,
});

const RewardCampaignScreen = (props) => {
  const { login } = props;
  const navigate = useNavigate();
  const [company, setCompany] = React.useState(login.company.master ? null : login.company.id);
  const [campaigns, setCampaigns] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(10);
  const [statusFilter, setStatusFilter] = React.useState(null);
  const [nameFilter, setNameFilter] = React.useState("");
  const [nameInput, setNameInput] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [companyBalance, setCompanyBalance] = React.useState(0);
  const [loadingBalance, setLoadingBalance] = React.useState(false);
  const [rewardBalance, setRewardBalance] = React.useState(0);
  const [loadingRewardBalance, setLoadingRewardBalance] = React.useState(false);
  const [formVisible, setFormVisible] = React.useState(false);
  const [configVisible, setConfigVisible] = React.useState(false);
  const [editingCampaign, setEditingCampaign] = React.useState(null);
  const [selectedCampaignKeys, setSelectedCampaignKeys] = React.useState([]);
  const [exportLoading, setExportLoading] = React.useState(false);
  const [qrVouchers, setQrVouchers] = React.useState([]);
  const [qrLoading, setQrLoading] = React.useState(false);
  const [shouldPrint, setShouldPrint] = React.useState(false);
  const qrPrintRef = React.useRef();

  React.useEffect(() => {
    if (login.company.master && !company) return;
    loadBalance();
    loadRewardBalance();
  }, [company]);

  React.useEffect(() => {
    if (login.company.master && !company) return;
    setPage(0);
    loadCampaigns(0, pageSize);
  }, [company, statusFilter, nameFilter, pageSize]);

  React.useEffect(() => {
    if (login.company.master && !company) return;
    if (page > 0) loadCampaigns(page, pageSize);
  }, [page]);

  const loadCampaigns = async (p = page, s = pageSize) => {
    setLoading(true);
    try {
      const res = await listCampaigns(login.company.master ? company : 0, statusFilter, nameFilter || null, p, s);
      if (res.success) {
        setCampaigns(res.data || []);
        setTotal(res.total || 0);
      }
    } catch (e) {
      message.error("Erro ao carregar campanhas");
    } finally {
      setLoading(false);
    }
  };

  const loadBalance = async () => {
    if ((login.company.master && !company) || !company) return;
    setLoadingBalance(true);
    try {
      const res = await getTotalBalance(login.company.master ? company : null);
      setCompanyBalance(res.balance);
    } catch (e) {
      // silently ignore
    } finally {
      setLoadingBalance(false);
    }
  };

  const loadRewardBalance = async () => {
    if ((login.company.master && !company) || !company) return;
    setLoadingRewardBalance(true);
    try {
      const res = await getRewardBalance(login.company.master ? company : null);
      setRewardBalance(res?.balance ?? 0);
    } catch (e) {
      // silently ignore
    } finally {
      setLoadingRewardBalance(false);
    }
  };

  const formatPhone = (phone) => {
    if (!phone) return "—";
    const d = phone.replace(/\D/g, "").replace(/^55/, "").slice(0, 11);
    if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
    if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
    return phone;
  };

  const formatIncomeRange = (code) =>
    MONTHLY_REVENUE_RANGE_DATA.find((x) => x.code === code)?.name || code || "—";

  const formatGender = (code) =>
    code === "M" ? "Masculino" : code === "F" ? "Feminino" : "Prefiro não informar";

  const handlePrintQrCodes = useReactToPrint({
    content: () => qrPrintRef.current,
    documentTitle: "qrcodes_campanhas",
  });

  React.useEffect(() => {
    if (shouldPrint && qrVouchers.length > 0) {
      handlePrintQrCodes();
      setShouldPrint(false);
    }
  }, [shouldPrint, qrVouchers]);

  const onGenerateQrCodes = async () => {
    setQrLoading(true);
    try {
      const res = await exportCampaigns(selectedCampaignKeys);
      if (!res.success) {
        message.error("Erro ao gerar QR codes.");
        return;
      }
      const actived = (res.vouchers || [])
        .filter((v) => v.status?.code === "actived")
        .map((v) => ({ ...v, _name: v.campaignName }));
      if (actived.length === 0) {
        message.warning("Nenhum voucher ativo nas campanhas selecionadas.");
        return;
      }
      setQrVouchers(actived);
      setShouldPrint(true);
    } catch (e) {
      message.error("Erro ao gerar QR codes.");
    } finally {
      setQrLoading(false);
    }
  };

  const onExportCampaigns = async () => {
    setExportLoading(true);
    try {
      const res = await exportCampaigns(selectedCampaignKeys);
      if (!res.success) {
        message.error("Erro ao exportar campanhas");
        return;
      }

      if ((res.vouchers || []).length === 0) {
        message.warning("As campanhas selecionadas não possuem lotes para exportar");
        return;
      }

      const rows = (res.vouchers || []).map((v) => {
        const rescued = v.status?.code === "rescued";
        return {
          Campanha: v.campaignName || "",
          Lote: v.batchName || "",
          "E-mail": (rescued ? v.redeemerEmail : v.email) || v.email || "",
          Telefone: rescued
            ? (v.redeemerPhone ? formatPhone(v.redeemerPhone) : v.phone || "")
            : v.phone || "",
          Valor: formatMoney(v.amount, false),
          "Criado em": v.createdAt ? formatDate(v.createdAt) : "",
          "Expira em": v.expirationDate ? formatDate(v.expirationDate) : "",
          Link: v.link || "",
          Status: v.status?.name || "",
          Nome: v.redeemerName || "",
          CPF: v.redeemerCpf ? getMaskedCpf(v.redeemerCpf) : "",
          "E-mail Resgatador": v.redeemerEmail || "",
          "Telefone Resgatador": v.redeemerPhone ? formatPhone(v.redeemerPhone) : "",
          Sexo: rescued ? formatGender(v.redeemerGender) : "",
          "Data de Nascimento": rescued && v.redeemerBirthdate ? formatDate(v.redeemerBirthdate) : "",
          Ocupação: v.redeemerOccupation || "",
          "Faixa Salarial": formatIncomeRange(v.redeemerIncomeRange),
          CEP: v.redeemerAddressZip || "",
          Rua: v.redeemerAddressStreet || "",
          Número: v.redeemerAddressNumber || "",
          Complemento: v.redeemerAddressComplement || "",
          Bairro: v.redeemerAddressNeighborhood || "",
          Cidade: v.redeemerAddressCity || "",
          UF: v.redeemerAddressState || "",
          "Data do Resgate": v.rescuedAt ? formatDate(v.rescuedAt) : "",
          "Banco Receptor": v.pixBankName || "",
          "Titular da Chave": v.pixHolderName || "",
          "Doc. do Titular": v.pixHolderDocument ? getMaskedCpf(v.pixHolderDocument) : "",
          "Chave PIX": v.pixKey || "",
          "Tipo da Chave": v.pixKeyType || "",
          "PIX Iniciado em": v.pixInitiatedAt ? formatDate(v.pixInitiatedAt, "DD/MM/YYYY HH:mm") : "",
          "PIX Confirmado em": v.pixConfirmedAt ? formatDate(v.pixConfirmedAt, "DD/MM/YYYY HH:mm") : "",
        };
      });

      exportXls().createAndSaveFile(
        ["Vouchers"],
        { Vouchers: rows },
        "exportacao_campanhas"
      );
    } catch (e) {
      message.error("Erro ao exportar campanhas");
    } finally {
      setExportLoading(false);
    }
  };

  const onEdit = (campaign) => {
    setEditingCampaign(campaign);
    setFormVisible(true);
  };

  const onFormClose = (saved) => {
    setFormVisible(false);
    setEditingCampaign(null);
    if (saved) loadCampaigns(page, pageSize);
  };

  const onCancelCampaign = (campaign) => {
    Modal.confirm({
      title: `Cancelar campanha "${campaign.name}"?`,
      content: "Todos os lotes ativos serão cancelados e o saldo devolvido à conta principal da empresa.",
      okText: "Sim, cancelar",
      okButtonProps: { danger: true },
      cancelText: "Não",
      onOk: async () => {
        try {
          await cancelCampaign(campaign.id);
          message.success("Campanha cancelada com sucesso");
          loadCampaigns(page, pageSize);
          loadBalance();
          loadRewardBalance();
        } catch (e) {
          message.error("Erro ao cancelar campanha");
        }
      },
    });
  };

  const redemptionTag = (type) => {
    const color = type?.code === "CARD" ? "blue" : "green";
    return <Tag color={color}>{type?.name || "-"}</Tag>;
  };

  const rowSelection = {
    selectedRowKeys: selectedCampaignKeys,
    onChange: (keys) => setSelectedCampaignKeys(keys),
  };

  const columns = [
    {
      title: "Nome da Campanha",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Tipo de Resgate",
      dataIndex: "redemptionType",
      key: "redemptionType",
      align: "center",
      render: (t) => redemptionTag(t),
    },
    {
      title: "Qtd. Lotes",
      dataIndex: "lotCount",
      key: "lotCount",
      align: "center",
    },
    {
      title: "Qtd. Prêmios",
      dataIndex: "prizeCount",
      key: "prizeCount",
      align: "center",
    },
    {
      title: "Ticket Médio",
      dataIndex: "avgTicket",
      key: "avgTicket",
      align: "center",
      render: (v) => (v != null ? formatMoney(v) : "—"),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      align: "center",
      render: (status) => {
        const color = status?.code === "ACTIVE" ? "green" : status?.code === "CANCELLED" ? "red" : "default";
        return <Tag color={color}>{status?.name || "-"}</Tag>;
      },
    },
    {
      title: "Criado em",
      dataIndex: "createdAt",
      key: "createdAt",
      align: "center",
      render: (d) => (d ? formatDate(d, "DD/MM/YYYY") : "-"),
    },
    {
      title: "Ações",
      key: "actions",
      align: "center",
      render: (_, record) => (
        <>
          {record.status?.code !== "CANCELLED" && !login.company.master && (
            <Button type="link" onClick={() => onEdit(record)}>Editar</Button>
          )}
          <Button type="link" onClick={() => navigate(`/reward-voucher/${record.id}`)}>Ver Lotes</Button>
          {record.status?.code !== "CANCELLED" && !login.company.master && (
            <Button type="link" danger onClick={() => onCancelCampaign(record)}>Cancelar</Button>
          )}
        </>
      ),
    },
  ];

  return (
    <Layout className="alymente-layout">
      <Header
        title="Link+"
        middle={
          login.company.master ? (
            <AutoCompleteCompany value={company} onChange={setCompany} />
          ) : undefined
        }
      />
      <Content style={{ padding: 16 }}>
        {(!login.company.master || company) && (
          <Row style={{ marginBottom: 16 }} gutter={16}>
            <Col>
              <CardInfo
                style={{ minWidth: 300, minHeight: 115 }}
                loading={loadingBalance}
                title="Saldo Atual"
                value={formatMoney(companyBalance)}
              />
            </Col>
            <Col>
              <CardInfo
                style={{ minWidth: 300, minHeight: 115 }}
                loading={loadingRewardBalance}
                title="Saldo Reservado (Link+)"
                value={formatMoney(rewardBalance)}
              />
            </Col>
          </Row>
        )}
        <Row gutter={[12, 8]} align="middle" style={{ marginBottom: 12, marginTop: 8 }}>
          <Col>
            <Search
              placeholder="Buscar por nome"
              allowClear
              style={{ width: 240 }}
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              onSearch={(v) => { setNameInput(v); setNameFilter(v); }}
            />
          </Col>
          <Col>
            <Select
              allowClear
              placeholder="Status"
              style={{ width: 160 }}
              onChange={(v) => setStatusFilter(v || null)}
              options={[
                { value: "ACTIVE", label: "Ativo" },
                { value: "CANCELLED", label: "Cancelado" },
                { value: "INACTIVE", label: "Inativo" },
              ]}
            />
          </Col>
        </Row>
        <Table
          dataSource={campaigns}
          columns={columns}
          loading={loading}
          rowKey="id"
          rowSelection={rowSelection}
          pagination={{
            current: page + 1,
            pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: [5, 10, 20, 50, 100],
            onChange: (p, s) => { setPageSize(s); setPage(s !== pageSize ? 0 : p - 1); },
          }}
          bordered
          size="small"
          style={{ marginTop: 8 }}
        />
      </Content>
      <Footer style={{ display: "flex", justifyContent: "flex-end" }}>
        <Space>
          {selectedCampaignKeys.length > 0 && (
            <Button icon={<FilePdfOutlined />} loading={qrLoading} onClick={onGenerateQrCodes}>
              Gerar QR Codes
            </Button>
          )}
          {selectedCampaignKeys.length > 0 && (
            <Button icon={<DownloadOutlined />} onClick={onExportCampaigns} loading={exportLoading}>
              Exportar Relatório ({selectedCampaignKeys.length})
            </Button>
          )}
          {!login.company.master && (
            <Button
              icon={<SettingOutlined />}
              onClick={() => setConfigVisible(true)}
            >
              Configuração
            </Button>
          )}
          {!login.company.master && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => { setEditingCampaign(null); setFormVisible(true); }}
            >
              Nova Campanha
            </Button>
          )}
        </Space>
      </Footer>
      <div style={{ display: "none" }}>
        <RewardVoucherQrCodePrint ref={qrPrintRef} vouchers={qrVouchers} />
      </div>
      <RewardCampaignForm
        visible={formVisible}
        campaign={editingCampaign}
        onClose={onFormClose}
      />
      <RewardCampaignConfigDrawer
        visible={configVisible}
        onClose={() => setConfigVisible(false)}
      />
    </Layout>
  );
};

export default connect(mapStateToProps, {})(RewardCampaignScreen);
